/**
 * 贴纸面板纯逻辑（14.16a；两线程共用）：sticker/mine 快照形状、最近发送
 * 去重置顶、发送 envelope 构造。
 *
 * 协议口径（protocol §11 + 旧版实锤）：
 * - 用户自传贴纸发送走 **msgType=2 图片兼容通道**（旧版 _sendRemoteStickerMessage：
 *   旧客户端不认识 kind 字段按普通图片渲染可见；新版靠 kind=sticker 还原贴纸
 *   形态——messageModel.stickerPayload 已解析）。msgType 44 是已废弃的内置包
 *   专用类型，新版不发送（渲染历史消息按占位，msgTypes 既有）。
 * - 最近发送每人保留 20 条、同贴纸去重置顶（服务端 sticker_recent 同规则，
 *   本地先行乐观维护，上报失败两边短暂分叉、下次 mine 对账找平）。
 */

/** 包内单张贴纸（sticker/mine Items 行收窄；path=去域名相对路径） */
export interface StickerItem {
  readonly id: number;
  readonly path: string;
  readonly width: number;
  readonly height: number;
}

/**
 * 篮子里的包（面板导航条用 cover，网格用 items）。author/intro 是 14.17a
 * 追加（包预览 sheet 篮子命中零 HTTP 时要展示）：旧 kv 缓存的快照可能缺省，
 * 读侧折 ''，下次 mine 刷新找平。
 */
export interface StickerPack {
  readonly id: number;
  readonly author?: string;
  readonly intro?: string;
  readonly cover: string;
  readonly items: readonly StickerItem[];
}

/** 最近发送行（mine.Recent 行收窄；发送/上报也用这个形状） */
export interface RecentSticker {
  readonly packId: number;
  readonly itemId: number;
  readonly path: string;
  readonly width: number;
  readonly height: number;
}

/** sticker/mine 整包快照（面板一次拉全，protocol §11） */
export interface StickerMineSnapshot {
  readonly packs: readonly StickerPack[];
  readonly recent: readonly RecentSticker[];
}

// ---- 贴纸包管理（14.17a；创作/审核/篮子，protocol §11） ----

/** 审核状态（后端 models.StickerStatus*：0 待审 / 1 已过审 / 2 已拒） */
export const STICKER_STATUS_PENDING = 0;
export const STICKER_STATUS_APPROVED = 1;
export const STICKER_STATUS_REJECTED = 2;

/** 创作/详情视角的完整包行（sticker/created、sticker/pack 收窄） */
export interface CreatedStickerPack {
  readonly id: number;
  readonly author: string;
  readonly intro: string;
  readonly cover: string;
  readonly status: number;
  readonly rejectReason: string;
  readonly items: readonly StickerItem[];
}

/**
 * 创建/修改包的待提交条目（sticker/upload 返回，或编辑模式复用远端原图——
 * 复用时 size 取 0：created/pack 响应不带 Size，服务端只校验 >100KB 上限，
 * 旧版同口径）。
 */
export interface StickerDraftItem {
  readonly path: string;
  readonly width: number;
  readonly height: number;
  readonly size: number;
}

/** 创建（packId 缺省）或修改重审（带 packId，仅待审/被拒的包）的提交体 */
export interface StickerPackDraft {
  readonly packId?: number;
  readonly author: string;
  readonly intro: string;
  readonly items: readonly StickerDraftItem[];
}

/** 创建约束（func_sticker.go 同值；服务端兜底校验，前端预检省无效上传） */
export const STICKER_MAX_BYTES = 100 * 1024;
export const STICKER_PACK_MAX_ITEMS = 20;
export const STICKER_INTRO_MAX_RUNES = 10;
export const STICKER_AUTHOR_MAX_RUNES = 20;

const STICKER_ALLOWED_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

/**
 * 选图预检（旧版 _pickImages 同口径：按文件名扩展名+大小拦截，真实内容由
 * 服务端按 MIME 检测兜底）。返回拒绝原因文案，null=通过。
 */
export function stickerFileRejection(name: string, size: number): string | null {
  const dot = name.lastIndexOf('.');
  const ext = dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
  if (!STICKER_ALLOWED_EXTENSIONS.has(ext)) return '仅支持 png / jpg / gif / webp 图片';
  if (size <= 0 || size > STICKER_MAX_BYTES) return '单张表情不能超过 100KB';
  return null;
}

/** 完整包行 → 面板篮子形态（add 成功后本地追加 mine 快照用） */
export function basketPackOf(pack: CreatedStickerPack): StickerPack {
  return {
    id: pack.id,
    author: pack.author,
    intro: pack.intro,
    cover: pack.cover,
    items: pack.items,
  };
}

/** 每人最近发送保留条数（服务端 stickerRecentKeep 同值） */
export const RECENT_STICKERS_MAX = 20;

/** 最近发送去重置顶（旧版 recordSent 同口径：按 itemId 去重、新在前、截断） */
export function recordRecentSticker(
  recent: readonly RecentSticker[],
  sent: RecentSticker,
): RecentSticker[] {
  const next = [sent, ...recent.filter((r) => r.itemId !== sent.itemId)];
  return next.length > RECENT_STICKERS_MAX ? next.slice(0, RECENT_STICKERS_MAX) : next;
}

/**
 * 贴纸发送 envelope（msgType=2 兼容通道；旧版 _sendRemoteStickerMessage 逐字段
 * 同形）：w/h 仅 >0 才带、pack_id/sticker_id 是服务端数字 id 的字符串形式、
 * clock>0=阅后即焚秒数（仅私聊，调用方按会话 afterViewTime 传）。
 */
export function buildStickerEnvelope(sent: RecentSticker, clock = 0): string {
  const body: Record<string, unknown> = { url: sent.path };
  if (sent.width > 0) body['w'] = sent.width;
  if (sent.height > 0) body['h'] = sent.height;
  body['kind'] = 'sticker';
  body['source'] = 'remote';
  body['pack_id'] = String(sent.packId);
  body['sticker_id'] = String(sent.itemId);
  if (clock > 0) body['clock'] = clock;
  return JSON.stringify(body);
}
